/**
 * La logique PURE partagée du planning (lot PL1a, ADR-0015 ; ce qui reste après le retrait de l'ancien planning par
 * visites, ADR-0016, lot U6) : les jours et les semaines, les durées, et le formulaire de création d'une intervention.
 * Les gestes et ce que montre chaque case vivent dans `planning-interventions.ts`. Sans React, sans appel réseau : le
 * bureau n'a pas de test de composants, tout ce qui peut se tromper vit donc ici, testé.
 *
 * Une règle de fond : l'écran n'invente RIEN de ce que décide le serveur (« aujourd'hui », « en retard »…). Les jours
 * sont des chaînes AAAA-MM-JJ, jamais des instants : tout le calcul se fait en UTC sur des minuits, aucun changement
 * d'heure ne s'y glisse. Vocabulaire hérité : `FormulaireVisite` et `VisiteMachine` gardent leur nom (renommage hors lot).
 */
import type { Asset, GenreVisite, Site, VisiteMachine } from '../api/client';
import { cleRecherche, jetonsDeRecherche } from './gammes-recherche';

// --- Les jours -------------------------------------------------------------------------------------

const MS_PAR_JOUR = 86_400_000;
const FORME_JOUR = /^\d{4}-\d{2}-\d{2}$/;

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
] as const;
const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'] as const;
const JOURS_COURTS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'] as const;
const JOURS_LONGS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'] as const;

function ms(jour: string): number {
  return Date.parse(`${jour}T00:00:00Z`);
}

/** Un jour AAAA-MM-JJ qui existe vraiment (le 30 février passerait une simple expression régulière). */
export function jourValide(jour: string): boolean {
  if (!FORME_JOUR.test(jour)) return false;
  const t = ms(jour);
  return !Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === jour;
}

/** Le jour `n` jours après (ou avant, si négatif) `jour`. */
export function ajouterJours(jour: string, n: number): string {
  return new Date(ms(jour) + Math.round(n) * MS_PAR_JOUR).toISOString().slice(0, 10);
}

/** Le lundi de la semaine qui contient `jour` (semaine ISO : elle commence le lundi). */
export function lundiDe(jour: string): string {
  const dow = new Date(ms(jour)).getUTCDay(); // 0 = dimanche
  return ajouterJours(jour, -((dow + 6) % 7));
}

/** Les sept jours (lundi → dimanche) de la semaine qui commence le `lundi`. */
export function joursDeLaSemaine(lundi: string): string[] {
  return Array.from({ length: 7 }, (_, i) => ajouterJours(lundi, i));
}

export function estWeekend(jour: string): boolean {
  const dow = new Date(ms(jour)).getUTCDay();
  return dow === 0 || dow === 6;
}

/** Le numéro de semaine ISO 8601 : la semaine 1 est celle qui contient le premier jeudi de l'année. */
export function numeroSemaineIso(jour: string): number {
  const d = new Date(ms(jour));
  const jeudi = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 4 - (d.getUTCDay() || 7)));
  const debutAnnee = Date.UTC(jeudi.getUTCFullYear(), 0, 1);
  return Math.ceil(((jeudi.getTime() - debutAnnee) / MS_PAR_JOUR + 1) / 7);
}

/** « 26/10/2026 ». */
export function jourFr(jour: string): string {
  return jour.split('-').reverse().join('/');
}

/**
 * « lun. 26 oct. » : l'en-tête d'une colonne. Le MOIS y figure toujours (demande de Benjamin, 2026-10-20) : un « lundi 19 »
 * seul peut être d'octobre, de novembre ou de septembre, et le numéro de semaine ne le dit pas.
 */
export function jourCourt(jour: string): string {
  const d = new Date(ms(jour));
  return `${JOURS_COURTS[d.getUTCDay()] ?? ''} ${d.getUTCDate()} ${MOIS_COURTS[d.getUTCMonth()] ?? ''}`;
}

/** « lundi 26 octobre » : pour une phrase. */
export function jourLong(jour: string): string {
  const d = new Date(ms(jour));
  return `${JOURS_LONGS[d.getUTCDay()] ?? ''} ${d.getUTCDate()} ${MOIS[d.getUTCMonth()] ?? ''}`;
}

/** « Semaine 44 · 26 octobre → 1 novembre 2026 ». */
export function libelleSemaine(lundi: string): string {
  const debut = new Date(ms(lundi));
  const fin = new Date(ms(ajouterJours(lundi, 6)));
  const memeMois = debut.getUTCMonth() === fin.getUTCMonth();
  const memeAnnee = debut.getUTCFullYear() === fin.getUTCFullYear();
  const gauche = `${debut.getUTCDate()}${memeMois ? '' : ` ${MOIS[debut.getUTCMonth()] ?? ''}`}${memeAnnee ? '' : ` ${debut.getUTCFullYear()}`}`;
  const droite = `${fin.getUTCDate()} ${MOIS[fin.getUTCMonth()] ?? ''} ${fin.getUTCFullYear()}`;
  return `Semaine ${numeroSemaineIso(lundi)} · ${gauche} → ${droite}`;
}

// --- Durées ----------------------------------------------------------------------------------------

/** « 8 h », « 1 h 30 », « 45 min ». */
export function dureeFr(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const reste = minutes % 60;
  return reste === 0 ? `${h} h` : `${h} h ${String(reste).padStart(2, '0')}`;
}

export const DUREE_MIN_MINUTES = 15;
export const DUREE_MAX_MINUTES = 1440;

/**
 * Lit une durée saisie à la française, en heures : « 8 », « 7,5 », « 1h30 », « 1 h 30 », « 45 min », « 90 min ».
 * Vide : `null` (pas de durée prévue). Illisible ou hors des bornes (15 minutes à 24 heures) : `'ERREUR'`.
 */
export function lireDuree(texte: string, minimum: number = DUREE_MIN_MINUTES): number | null | 'ERREUR' {
  const t = texte.trim().toLowerCase().replace(/\s+/g, ' ');
  if (t === '') return null;

  let minutes: number | null = null;
  let m = /^(\d+)\s*(?:h|heures?)\s*(\d{1,2})?$/.exec(t);
  if (m) {
    const reste = m[2] === undefined ? 0 : Number(m[2]);
    // « 1h75 » n'est pas une durée : les minutes vont de 0 à 59.
    if (reste >= 60) return 'ERREUR';
    minutes = Number(m[1]) * 60 + reste;
  } else if ((m = /^(\d+)\s*(?:min|minutes?|mn)$/.exec(t))) {
    minutes = Number(m[1]);
  } else if ((m = /^(\d+(?:[.,]\d+)?)$/.exec(t))) {
    // Un nombre seul se lit en HEURES (« 8 » = 8 h, « 7,5 » = 7 h 30).
    minutes = Math.round(Number((m[1] as string).replace(',', '.')) * 60);
  }
  if (minutes === null || !Number.isFinite(minutes) || minutes < minimum || minutes > DUREE_MAX_MINUTES) return 'ERREUR';
  return minutes;
}

/** La durée d'une visite pour un champ de saisie : « 8 », « 7,5 », vide si inconnue. */
export function dureePourSaisie(minutes: number | null): string {
  if (minutes === null) return '';
  const heures = minutes / 60;
  return Number.isInteger(heures) ? String(heures) : String(Math.round(heures * 100) / 100).replace('.', ',');
}

/** « AIR01 », sinon la désignation, sinon le modèle et le n° de série. */
export function libelleMachine(m: VisiteMachine): string {
  return m.clientDesignation ?? m.label ?? ([m.model, m.serialNumber ? `n° ${m.serialNumber}` : null].filter(Boolean).join(' ') || 'Machine');
}

/** « dans 8 j », « demain », « aujourd'hui », « en retard de 1 j », d'après les jours restants rendus par le serveur. */
export function echeanceTexte(joursRestants: number | null): string | null {
  if (joursRestants === null) return null;
  if (joursRestants === 0) return 'aujourd’hui';
  if (joursRestants === 1) return 'demain';
  if (joursRestants > 1) return `dans ${joursRestants} j`;
  return `en retard de ${-joursRestants} j`;
}

/** Les initiales d'un nom : « Gregory BONFILS » → « GB ». */
export function initiales(nom: string): string {
  const mots = nom.trim().split(/\s+/).filter((m) => m !== '');
  const [premier, dernier] = [mots[0], mots[mots.length - 1]];
  if (!premier) return '?';
  if (mots.length === 1 || !dernier) return premier.slice(0, 2).toUpperCase();
  return `${premier[0] ?? ''}${dernier[0] ?? ''}`.toUpperCase();
}

// --- Le formulaire de création --------------------------------------------------------------------

/** Les trois façons de poser une visite : le coeur du planning (voir ADR-0015). */
export type ModePlanification = 'DATE_TECHNICIEN' | 'DATE_FIXE' | 'FENETRE';

export const LIBELLE_MODE: Readonly<Record<ModePlanification, string>> = {
  DATE_TECHNICIEN: 'Planifier à une date, avec un technicien',
  DATE_FIXE: 'Date fixe, technicien plus tard',
  FENETRE: 'Fenêtre d’échéance, sans date',
};

export interface MachineChoisie {
  readonly assetId: string;
  readonly libelle: string;
  /** Le site de la machine (une visite n'a qu'un site : un seul déplacement). */
  readonly siteId: string | null;
  readonly siteLibelle: string | null;
  /**
   * Le palier de gamme à faire sur cette machine (lot PL4) : l'identité stable du palier, jamais son code recopié. `null` ou absent =
   * pas encore choisi : la visite reste valable, simplement sans liste de pièces. Jamais présélectionné : il se choisit.
   */
  readonly levelKey?: string | null;
}

export interface FormulaireVisite {
  readonly genre: GenreVisite;
  readonly siteId: string | null;
  readonly siteLibelle: string | null;
  readonly machines: readonly MachineChoisie[];
  readonly natureId: string;
  readonly titre: string;
  readonly technicienIds: readonly string[];
  readonly mode: ModePlanification;
  readonly date: string;
  readonly fenetreDebut: string;
  readonly fenetreFin: string;
  readonly dureeTexte: string;
  readonly description: string;
  readonly commande: string;
  /** « À confirmer » : pré-planifiée, la date n'est pas définitive. */
  readonly tentative: boolean;
}

export function formulaireVide(jour = ''): FormulaireVisite {
  return {
    genre: 'CUSTOMER',
    siteId: null,
    siteLibelle: null,
    machines: [],
    natureId: '',
    titre: '',
    technicienIds: [],
    mode: 'DATE_TECHNICIEN',
    date: jour,
    fenetreDebut: '',
    fenetreFin: '',
    dureeTexte: '',
    description: '',
    commande: '',
    tentative: false,
  };
}

export const PLAFOND_DESCRIPTION = 1000;
export const PLAFOND_COMMANDE = 60;
export const PLAFOND_TITRE = 120;

/** Les messages à montrer avant d'envoyer (le serveur contrôle de toute façon). Vide = prêt. */
export function validerFormulaire(f: FormulaireVisite): string[] {
  const m: string[] = [];
  if (f.genre === 'WORKSHOP') {
    if (f.titre.trim() === '') m.push('Donnez un titre à la visite d’atelier.');
    else if (f.titre.trim().length > PLAFOND_TITRE) m.push(`Le titre : ${PLAFOND_TITRE} caractères au plus.`);
  } else {
    if (f.siteId === null && f.machines.length === 0) m.push('Choisissez une machine ou un site.');
    if (f.natureId === '') m.push('Choisissez la nature de la visite (entretien, dépannage…).');
    const sites = new Set(f.machines.map((x) => x.siteId));
    if (sites.size > 1) m.push('Toutes les machines d’une visite sont du même site (un seul déplacement).');
    if (f.machines.some((x) => x.siteId === null)) m.push('Une machine n’a pas de site : rattachez-la d’abord à un site.');
  }

  if (f.mode === 'FENETRE') {
    if (!jourValide(f.fenetreDebut) || !jourValide(f.fenetreFin)) m.push('Donnez le début ET la fin de la fenêtre.');
    else if (f.fenetreFin < f.fenetreDebut) m.push('La fenêtre ne peut pas finir avant de commencer.');
  } else {
    if (!jourValide(f.date)) m.push('Choisissez la date.');
    if (f.mode === 'DATE_TECHNICIEN' && f.technicienIds.length === 0) {
      m.push('Choisissez au moins un technicien — ou « Date fixe, technicien plus tard ».');
    }
  }

  if (lireDuree(f.dureeTexte) === 'ERREUR') m.push('La durée : de 15 minutes à 24 heures (« 8 », « 7,5 », « 1h30 », « 45 min »).');
  if (f.description.trim().length > PLAFOND_DESCRIPTION) m.push(`La description : ${PLAFOND_DESCRIPTION} caractères au plus.`);
  if (f.commande.trim().length > PLAFOND_COMMANDE) m.push(`Le n° de commande : ${PLAFOND_COMMANDE} caractères au plus.`);
  return m;
}

// --- Chercher dans le volet : machines et sites, « ntn sey » ---------------------------------------

/** On ne cherche qu'à partir de ce nombre de caractères : une requête par lettre serait du bruit. */
export const CARACTERES_MINIMUM = 2;

/**
 * Le mot à envoyer au serveur : les routes de recherche existantes ne trouvent que la chaîne ENTIÈRE dans un champ
 * (« ntn sey » ne trouve rien tel quel). On interroge donc avec le mot le plus long — le plus sélectif — et on filtre
 * ensuite côté écran sur TOUS les mots (`filtrerMachines`, `filtrerSites`). `null` : rien d'assez long pour chercher.
 */
export function motPourRecherche(saisie: string): string | null {
  let meilleur: string | null = null;
  for (const mot of saisie.split(/\s+/)) {
    if (cleRecherche(mot).length < CARACTERES_MINIMUM) continue;
    if (meilleur === null || mot.length > meilleur.length) meilleur = mot;
  }
  return meilleur;
}

function tousLesMotsSeRetrouvent(saisie: string, champs: readonly (string | null | undefined)[]): boolean {
  const jetons = jetonsDeRecherche(saisie);
  if (jetons.length === 0) return true;
  const cles = champs.map((c) => cleRecherche(c ?? '')).filter((c) => c !== '');
  return jetons.every((j) => cles.some((c) => c.includes(j)));
}

/** Les machines dont chaque mot de la saisie se retrouve quelque part (nom, modèle, n° de série, client, site, ville…). */
export function filtrerMachines(machines: readonly Asset[], saisie: string): Asset[] {
  return machines.filter((m) =>
    tousLesMotsSeRetrouvent(saisie, [
      m.clientDesignation,
      m.label,
      m.model,
      m.manufacturerReference,
      m.serialNumber,
      m.brandName,
      m.customerName,
      m.siteName,
      m.siteCity,
    ]),
  );
}

/** Les sites dont chaque mot de la saisie se retrouve quelque part (nom, code, client, ville, code postal). */
export function filtrerSites(sites: readonly Site[], saisie: string): Site[] {
  return sites.filter((s) => tousLesMotsSeRetrouvent(saisie, [s.name, s.code, s.customerName, s.city, s.postalCode]));
}

/** « AIR01 », sinon la désignation, sinon le modèle et le n° de série. */
export function libelleDeMachine(m: {
  readonly clientDesignation: string | null;
  readonly label: string | null;
  readonly model: string | null;
  readonly serialNumber: string | null;
}): string {
  return m.clientDesignation ?? m.label ?? ([m.model, m.serialNumber ? `n° ${m.serialNumber}` : null].filter(Boolean).join(' ') || 'Machine');
}

export function machineChoisie(a: Asset): MachineChoisie {
  return { assetId: a.id, libelle: libelleDeMachine(a), siteId: a.siteId, siteLibelle: a.siteName };
}

// --- Composer le formulaire : chaque geste est une fonction pure -----------------------------------

/**
 * Pourquoi cette machine ne peut pas être ajoutée à la visite ; `null` quand elle le peut. Une visite est UN déplacement :
 * toutes ses machines sont du même site, et une machine sans site doit d'abord être rattachée à un site.
 */
export function refusAjoutMachine(f: FormulaireVisite, machine: MachineChoisie): string | null {
  if (machine.siteId === null) return 'Cette machine n’a pas de site : rattachez-la d’abord à un site dans Le Parc.';
  if (f.machines.some((m) => m.assetId === machine.assetId)) return 'Cette machine est déjà dans la visite.';
  const siteDeLaVisite = f.machines[0]?.siteId ?? f.siteId;
  if (siteDeLaVisite !== null && siteDeLaVisite !== undefined && siteDeLaVisite !== machine.siteId) {
    return 'Cette machine est d’un autre site : une visite est un seul déplacement. Créez une autre visite.';
  }
  return null;
}

/** Ajoute la machine (et prend son site). Sans effet quand elle est refusée : l'appelant montre `refusAjoutMachine`. */
export function ajouterMachine(f: FormulaireVisite, machine: MachineChoisie): FormulaireVisite {
  if (refusAjoutMachine(f, machine) !== null) return f;
  return { ...f, machines: [...f.machines, machine], siteId: machine.siteId, siteLibelle: machine.siteLibelle };
}

/** Retire la machine ; le site reste choisi (on peut avoir choisi le site d'abord). */
export function retirerMachine(f: FormulaireVisite, assetId: string): FormulaireVisite {
  return { ...f, machines: f.machines.filter((m) => m.assetId !== assetId) };
}

/** Choisit (ou efface, avec `null`) le palier de gamme d'UNE machine de la visite ; les autres machines n'y touchent pas. */
export function choisirPalier(f: FormulaireVisite, assetId: string, levelKey: string | null): FormulaireVisite {
  return { ...f, machines: f.machines.map((m) => (m.assetId === assetId ? { ...m, levelKey } : m)) };
}

/** Choisir un site : refusé quand des machines d'un AUTRE site sont déjà dans la visite. */
export function refusChoixSite(f: FormulaireVisite, siteId: string): string | null {
  const siteDesMachines = f.machines[0]?.siteId ?? null;
  if (siteDesMachines !== null && siteDesMachines !== siteId) {
    return 'Des machines d’un autre site sont déjà dans la visite : retirez-les d’abord.';
  }
  return null;
}

export function choisirSite(f: FormulaireVisite, site: { readonly id: string; readonly libelle: string }): FormulaireVisite {
  if (refusChoixSite(f, site.id) !== null) return f;
  return { ...f, siteId: site.id, siteLibelle: site.libelle };
}

/** On repart d'un lieu neuf : ni site, ni machines. */
export function oublierLieu(f: FormulaireVisite): FormulaireVisite {
  return { ...f, siteId: null, siteLibelle: null, machines: [] };
}

/** Coche ou décoche un technicien. */
export function basculerTechnicienFormulaire(f: FormulaireVisite, technicienId: string): FormulaireVisite {
  const present = f.technicienIds.includes(technicienId);
  return { ...f, technicienIds: present ? f.technicienIds.filter((id) => id !== technicienId) : [...f.technicienIds, technicienId] };
}

/** Choisit le mode ; « Date fixe, technicien plus tard » vide les techniciens (une date sans technicien n'a pas d'équipe). */
export function choisirMode(f: FormulaireVisite, mode: ModePlanification): FormulaireVisite {
  return { ...f, mode, ...(mode === 'DATE_FIXE' ? { technicienIds: [] } : {}) };
}

/**
 * La date proposée d'office : AUJOURD'HUI (Benjamin, 2026-10-27 : « la date est toujours au lendemain, il faut la corriger ») ;
 * elle reste modifiable dans le formulaire.
 */
export function dateParDefaut(aujourdhui: string): string {
  return aujourdhui;
}

/** Le formulaire a-t-il été touché depuis son départ ? Pour ne pas fermer sur un travail non enregistré. */
export function formulaireModifie(f: FormulaireVisite, depart: FormulaireVisite): boolean {
  const memeListe = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i]);
  return !(
    f.genre === depart.genre &&
    f.siteId === depart.siteId &&
    memeListe(
      f.machines.map((m) => `${m.assetId}|${m.levelKey ?? ''}`),
      depart.machines.map((m) => `${m.assetId}|${m.levelKey ?? ''}`),
    ) &&
    f.natureId === depart.natureId &&
    f.titre === depart.titre &&
    memeListe(f.technicienIds, depart.technicienIds) &&
    f.mode === depart.mode &&
    f.date === depart.date &&
    f.fenetreDebut === depart.fenetreDebut &&
    f.fenetreFin === depart.fenetreFin &&
    f.dureeTexte === depart.dureeTexte &&
    f.description === depart.description &&
    f.commande === depart.commande
  );
}

/** Le formulaire de départ d'une visite ouverte depuis une case de la grille : sa date, et son technicien (ou « À affecter »). */
export function formulaireDepuisCase(jour: string, technicienId: string | null): FormulaireVisite {
  const base = formulaireVide(jour);
  return technicienId === null ? { ...base, mode: 'DATE_FIXE' } : { ...base, mode: 'DATE_TECHNICIEN', technicienIds: [technicienId] };
}

/** Le formulaire de départ d'une visite ouverte depuis la fiche d'une machine. */
export function formulaireDepuisMachine(machine: MachineChoisie, aujourdhui: string): FormulaireVisite {
  return ajouterMachine(formulaireVide(dateParDefaut(aujourdhui)), machine);
}
